import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const CashierSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cashiers, setCashiers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    pin: '',
    permissions: ['billing', 'view_products']
  });

  // Module-based permissions for cashiers
  const availablePermissions = [
    { id: 'billing', label: 'POS Billing', description: 'Create and manage bills', module: 'core' },
    { id: 'estimations', label: 'Estimations', description: 'Create and view estimations', module: 'core' },
    { id: 'view_products', label: 'View Products', description: 'View product catalog', module: 'products' },
    { id: 'edit_products', label: 'Edit Products', description: 'Add/edit products and stock', module: 'products' },
    { id: 'cash_register', label: 'Cash Register', description: 'Open, close and manage cash register', module: 'cash' },
    { id: 'payment_collection', label: 'Payment Collection', description: 'Collect customer payments (requires admin approval)', module: 'cash' },
    { id: 'expenses', label: 'Expenses', description: 'Add and view expenses', module: 'finance' },
    { id: 'bills_history', label: 'Bills History', description: 'View bills and estimations history', module: 'reports' },
    { id: 'view_reports', label: 'View Reports', description: 'Access sales reports', module: 'reports' },
    { id: 'customer_ledger', label: 'Customer Ledger', description: 'View and manage customer credits', module: 'ledger' },
    { id: 'supplier_ledger', label: 'Supplier Ledger', description: 'View and manage supplier payments', module: 'ledger' },
    { id: 'grn_entry', label: 'GRN Entry', description: 'Create purchase entries', module: 'inventory' },
    { id: 'stock_management', label: 'Stock Management', description: 'Manage inventory stock', module: 'inventory' },
    { id: 'manage_customers', label: 'Manage Customers', description: 'Add/edit customers', module: 'core' },
    { id: 'apply_discounts', label: 'Apply Discounts', description: 'Apply discounts on bills', module: 'billing' },
    { id: 'view_dashboard', label: 'Dashboard', description: 'View business dashboard', module: 'reports' }
  ];

  // Group permissions by module
  const permissionGroups = {
    core: { label: 'Core Features', permissions: availablePermissions.filter(p => p.module === 'core') },
    billing: { label: 'Billing', permissions: availablePermissions.filter(p => p.module === 'billing') },
    cash: { label: 'Cash Management', permissions: availablePermissions.filter(p => p.module === 'cash') },
    finance: { label: 'Finance', permissions: availablePermissions.filter(p => p.module === 'finance') },
    reports: { label: 'Reports & History', permissions: availablePermissions.filter(p => p.module === 'reports') },
    ledger: { label: 'Ledgers', permissions: availablePermissions.filter(p => p.module === 'ledger') },
    inventory: { label: 'Inventory', permissions: availablePermissions.filter(p => p.module === 'inventory') },
    products: { label: 'Products', permissions: availablePermissions.filter(p => p.module === 'products') }
  };

  useEffect(() => {
    fetchCashiers();
  }, []);

  const fetchCashiers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/cashiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCashiers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load cashiers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (permId) => {
    setFormData(prev => {
      const perms = prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId];
      return { ...prev, permissions: perms };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate PIN
    if (!editingId && (formData.pin.length < 4 || formData.pin.length > 6)) {
      setError('PIN must be 4-6 digits');
      return;
    }

    try {
      const token = localStorage.getItem('pos_token');
      const submitData = { ...formData };
      
      // If editing and no new PIN, remove it from request
      if (editingId && !formData.pin) {
        delete submitData.pin;
      }
      
      if (editingId) {
        await axios.put(`${API}/cashiers/${editingId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Cashier updated successfully');
      } else {
        await axios.post(`${API}/cashiers`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Cashier created successfully');
      }
      
      fetchCashiers();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save cashier');
    }
  };

  const handleEdit = (cashier) => {
    setFormData({
      name: cashier.name,
      mobile: cashier.mobile,
      pin: '', // Don't show PIN, leave empty for optional update
      permissions: cashier.permissions || ['billing', 'view_products']
    });
    setEditingId(cashier.id);
    setShowForm(true);
  };

  const handleToggleStatus = async (id) => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.put(`${API}/cashiers/${id}/status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message);
      fetchCashiers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update cashier status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cashier?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/cashiers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Cashier deleted successfully');
      fetchCashiers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete cashier');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      pin: '',
      permissions: ['billing', 'view_products']
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="cashier-settings-page" className="max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Cashiers</h1>
          <p className="text-slate-500">Manage cashier accounts and permissions</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      {/* Add Button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="mb-6 px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2"
          data-testid="add-cashier-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Cashier
        </button>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {editingId ? 'Edit Cashier' : 'Add Cashier'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cashier Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., John"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="cashier-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number *</label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  placeholder="e.g., 9876543210"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="mobile-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  PIN {editingId ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  required={!editingId}
                  placeholder="4-6 digit PIN"
                  maxLength={6}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="pin-input"
                />
              </div>
            </div>

            {/* Permissions - Grouped by Module */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Module Access Permissions</label>
              <div className="space-y-4">
                {Object.entries(permissionGroups).map(([groupKey, group]) => (
                  group.permissions.length > 0 && (
                    <div key={groupKey} className="border border-slate-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">{group.label}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {group.permissions.map(perm => (
                          <label
                            key={perm.id}
                            className={`flex items-start gap-2 p-2 border rounded cursor-pointer transition-all ${
                              formData.permissions.includes(perm.id)
                                ? 'border-violet-300 bg-violet-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(perm.id)}
                              onChange={() => handlePermissionChange(perm.id)}
                              className="mt-0.5 w-4 h-4 text-violet-600 rounded"
                              data-testid={`perm-${perm.id}`}
                            />
                            <div>
                              <p className="font-medium text-slate-800 text-xs">{perm.label}</p>
                              <p className="text-xs text-slate-500">{perm.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, permissions: availablePermissions.map(p => p.id) }))}
                  className="text-sm text-violet-600 hover:text-violet-700"
                >
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, permissions: ['billing'] }))}
                  className="text-sm text-violet-600 hover:text-violet-700"
                >
                  Select Minimal
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                data-testid="save-cashier"
              >
                {editingId ? 'Update' : 'Add'} Cashier
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="cancel-button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Cashiers List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Cashier</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Mobile</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Permissions</th>
                <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {cashiers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                    No cashiers found. Add one to get started.
                  </td>
                </tr>
              ) : (
                cashiers.map(cashier => (
                  <tr key={cashier.id} className="hover:bg-slate-50" data-testid={`cashier-${cashier.id}`}>
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-800">{cashier.name}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{cashier.mobile}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(cashier.permissions || []).slice(0, 3).map(perm => (
                          <span
                            key={perm}
                            className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                          >
                            {perm.replace('_', ' ')}
                          </span>
                        ))}
                        {(cashier.permissions || []).length > 3 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                            +{cashier.permissions.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleStatus(cashier.id)}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          cashier.is_active
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                        data-testid={`toggle-${cashier.id}`}
                      >
                        {cashier.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleEdit(cashier)}
                        className="text-violet-600 hover:text-violet-700 mr-3"
                        data-testid={`edit-${cashier.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(cashier.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-${cashier.id}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <p className="text-blue-800 text-sm">
          <strong>Cashier Login:</strong> Cashiers can log in using the Store ID, their mobile number, and PIN.
        </p>
      </div>
    </div>
  );
};

export default CashierSettings;
